/* eslint-disable @typescript-eslint/no-explicit-any */
import { memo, useState, useCallback, useEffect, useMemo } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { Grid, Stack, styled } from '@mui/material';
import DialogTextField from 'components/dialog/DialogTextField';
import DialogDatePicker from 'components/dialog/DialogDatePicker';
import useProjectInfoProvider from 'hooks/useProjectInfoProvider';
import { useProject } from 'hooks/useProject';
import { DateValidationError } from '@mui/x-date-pickers';
import { useAppDispatch } from 'store';
import { openDialog } from 'store/dialog/dialogSlice';
import FindUserDialog from 'components/dialog/FindUserDialog';
import isEmpty from 'lodash/isEmpty';
import { IProjectInfoCmnStateProps } from 'interface/project/projectInfo';
import { useLocation } from 'react-router-dom';
import FileUploadAction from './FileUploadAction';

const GridItem = styled(Grid)(() => ({
  paddingTop: '0px!important',
}));

function Stepper1(): JSX.Element {
  const dispatch = useAppDispatch();
  const { getCommCodes, getProjectUsersList } = useProject();
  const { inputs, onChange } = useProjectInfoProvider();
  const [errorProjectStartAt, setErrorProjectStartAt] = useState<DateValidationError | null>(null);
  const [errorProjectEndAt, setErrorProjectEndAt] = useState<DateValidationError | null>(null);

  const { data: commCodesData } = getCommCodes();
  const handleFindInfoByCodeValue = useCallback(
    (codeValue: string): string => {
      return commCodesData?.find(code => code.codeValue === codeValue)?.codeName || '-';
    },
    [commCodesData],
  );
  const {
    projectType: inputProjectType,
    projectName,
    compName,
    projectStartAt,
    projectEndAt,
    compAdminList,
    projectId,
    projectPartUserFileName,
  } = inputs;
  const location = useLocation();
  const { data: usersListData, isLoading } = getProjectUsersList(projectId || '');
  const totalCount = useMemo(() => {
    return usersListData?.totalCount ?? 0;
  }, [usersListData?.totalCount]);
  useEffect(() => {
    if (inputs.userExcel?.rowCount === 0 && !isLoading) {
      onChange('userExcel.rowCount', totalCount);
    }
  }, [inputs.userExcel?.rowCount, isLoading, onChange, totalCount]);

  useEffect(() => {
    if (errorProjectEndAt) {
      onChange('projectEndAt', null);
    }
  }, [errorProjectEndAt, onChange]);

  const handleChange = useCallback(
    (name: string, value: any) => {
      onChange(name, value);
    },
    [onChange],
  );

  const handleDateError = (name: string, value: any) => {
    if (name === 'projectStartAt') {
      setErrorProjectStartAt(value);
    } else if (name === 'projectEndAt') {
      setErrorProjectEndAt(value);
    }
  };

  const errorMessage = (error: any) => {
    switch (error) {
      case 'maxDate':
      case 'minDate': {
        return '년도를 다시 확인해주세요.';
      }

      case 'invalidDate': {
        return '날짜가 유효하지 않습니다.';
      }

      default: {
        return '';
      }
    }
  };
  const handleFindUser = () => {
    dispatch(
      openDialog({
        content: <FindUserDialog stateKey="compAdminList" onChange={onChange} />,
      }),
    );
  };

  const handleAccountsListByComma = useMemo(() => {
    if (!compAdminList || compAdminList.length === 0) {
      return '';
    }
    return compAdminList?.map(obj => obj.name)?.join(', ');
  }, [compAdminList]);

  const handleStepper1ValuesValidCheck = useMemo((): boolean => {
    const requiredKeys: (keyof IProjectInfoCmnStateProps)[] = [
      'projectType',
      'projectName',
      'projectStartAt',
      'projectEndAt',
      'compName',
      'compAdminList',
    ];
    return requiredKeys.every(key => {
      const tmpCheck = inputs[key];
      return !isEmpty(tmpCheck);
    });
  }, [inputs]);

  useEffect(() => {
    onChange(
      'isStep1Valid',
      location.state?.rowInfo
        ? handleStepper1ValuesValidCheck
        : handleStepper1ValuesValidCheck && inputs.userExcel.file != null && inputs.userExcel.rowCount > 0,
    );
  }, [handleStepper1ValuesValidCheck, inputs.userExcel.file, inputs.userExcel.rowCount, location?.state, onChange]);

  const handleFileReset = () => {
    onChange('userExcel', {
      file: null,
      rowCount: 0,
      excelData: [],
      action: '',
    });
  };
  const handleFileChange = ({ file, rows, action }: { file: File; rows: any[][]; action: string }) => {
    onChange('userExcel', {
      file,
      rowCount: rows.length,
      excelData: rows,
      action: action ?? 'new',
    });
  };

  return (
    <Grid item xs={12}>
      <Box sx={{ px: '1.625rem' }}>
        <Grid
          container
          spacing={2}
          sx={{
            mt: '1.5rem',
            width: '100%',
            display: 'flex',
            justifyContent: { xs: 'flex-start', lg: 'center' },
          }}
        >
          <GridItem item xs={10}>
            <DialogTextField
              label="프로젝트 유형"
              text={handleFindInfoByCodeValue(inputProjectType)}
              variant="outlined"
              name="projectType"
              disabled
            />
          </GridItem>
          <GridItem item xs={10}>
            <DialogTextField
              label="프로젝트 이름"
              text={projectName}
              variant="outlined"
              name="projectName"
              onChange={e => handleChange('projectName', e.target.value)}
            />
          </GridItem>
          <GridItem item xs={10}>
            <DialogTextField
              label="프로젝트 기업명"
              text={compName}
              variant="outlined"
              name="compName"
              onChange={e => handleChange('compName', e.target.value)}
            />
          </GridItem>
          <GridItem item xs={10}>
            <Stack
              sx={{ marginBottom: '20px', marginTop: '1px' }}
              spacing={2}
              direction={{ xs: 'column', sm: 'column', md: 'row' }}
              alignItems="flex-start"
            >
              <Typography
                sx={{
                  color: 'mind.black.A0',
                  minWidth: '100px',
                  typography: 's13w800',
                  maxWidth: { xs: '100%', sm: '100%', md: '100px' },
                  width: { xs: '100%', sm: '100%', md: '100px' },
                  mt: '12px!important',
                }}
                noWrap
              >
                프로젝트 기간
              </Typography>
              <DialogDatePicker
                label="시작일"
                labelWidth={40}
                name="projectStartAt"
                value={projectStartAt}
                errorMessage={errorMessage(errorProjectStartAt)}
                handleDateChange={handleChange}
                handleDateError={handleDateError}
              />
              <DialogDatePicker
                label="종료일"
                labelWidth={40}
                name="projectEndAt"
                value={projectEndAt}
                errorMessage={errorMessage(errorProjectEndAt)}
                handleDateChange={handleChange}
                handleDateError={handleDateError}
                minDate={projectStartAt as any}
              />
            </Stack>
          </GridItem>
          <GridItem item xs={10} sm={10} md={8}>
            <DialogTextField
              label="프로젝트 담당자"
              text={handleAccountsListByComma}
              variant="outlined"
              name="compAdminList"
            />
          </GridItem>
          <GridItem
            item
            xs={10}
            sm={10}
            md={2}
            alignItems="flex-start"
            justifyContent="center"
            sx={{ display: 'flex', marginBottom: '20px' }}
          >
            <Button
              onClick={handleFindUser}
              color="primary"
              variant="contained"
              sx={{ width: '100%', height: 41, textWrap: 'nowrap' }}
            >
              담당자 찾기
            </Button>
          </GridItem>
          <GridItem item xs={10} sm={10} md={8}>
            <FileUploadAction
              type="template1"
              label="명단 업로드"
              onReset={handleFileReset}
              onChange={handleFileChange}
            >
              <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mr: 2 }}>
                {inputs.userExcel?.file ? (
                  <Typography>{inputs.userExcel?.file.name}</Typography>
                ) : (
                  projectPartUserFileName || '파일을 선택해주세요'
                )}
              </Box>
            </FileUploadAction>
          </GridItem>
          <GridItem
            item
            xs={10}
            sm={10}
            md={2}
            alignItems="flex-start"
            justifyContent="center"
            sx={{ display: 'flex' }}
          >
            <Button
              color="primary"
              variant="outlined"
              sx={{
                width: '100%',
                height: 41,
                textWrap: 'nowrap',
                '&:hover': {
                  backgroundColor: 'transparent',
                  border: '1px solid rgba(49, 84, 234, 0.5)',
                  cursor: 'default',
                },
              }}
            >
              {inputs.userExcel?.rowCount || 0}명
            </Button>
          </GridItem>
        </Grid>
      </Box>
    </Grid>
  );
}

export default memo(Stepper1);
